/**
 * The page's side of the one-button Moodle connect (#679). Not rendered anywhere yet: the button needs the design canvas first.
 *
 * The browser extension's content script marks the page (`data-davinci-connector`) and answers a `postMessage` with a status
 * word. The Moodle token never reaches this code: the content script moves it from Moodle to the server in its own isolated
 * world, and only `connected`, `failed`, `cancelled` or `mismatch` comes back.
 */

export type MoodleExtensionStatus = 'connected' | 'failed' | 'cancelled' | 'mismatch';

const REQUEST_TYPE = 'davinci-connect-moodle';
const RESULT_TYPE = 'davinci-connect-moodle-result';

/** Long enough for the learner to sign in to Moodle (the extension itself gives up after five minutes). */
export const CONNECT_TIMEOUT_MS = 5 * 60 * 1000 + 15_000;

export function isConnectorInstalled(doc: Document = document): boolean {
  return doc.documentElement.dataset['davinciConnector'] === '1';
}

export interface MoodleExtensionResult {
  readonly status: MoodleExtensionStatus;
  /** The Moodle account's name, for display only. Untrusted text: render it as text, never trust it for anything else. */
  readonly moodleFullName?: string;
}

const NAME_MAX = 200;

function readName(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  return value.trim().slice(0, NAME_MAX);
}

function readStatus(event: MessageEvent, win: Window): { status: MoodleExtensionStatus; name?: string } | undefined {
  if (event.source !== win || event.origin !== win.location.origin) return undefined;
  const data: unknown = event.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const { type, status, moodleFullName } = data as { type?: unknown; status?: unknown; moodleFullName?: unknown };
  if (type !== RESULT_TYPE) return undefined;
  const name = readName(moodleFullName);
  return {
    status: status === 'connected' || status === 'cancelled' || status === 'mismatch' ? status : 'failed',
    ...(name !== undefined ? { name } : {}),
  };
}

/**
 * Asks the extension to connect Moodle. Resolves `failed` if it is not installed or does not answer within the timeout.
 *
 * The result message is a same-window `postMessage`, so any script on the page can forge it: `connected` is believed only
 * once `confirmConnected` (a `GET /moodle/connection`, i.e. the server's own answer) says so. A forged message can change
 * only the displayed name, which is untrusted text and is dropped unless the final status is `connected`.
 */
export function connectMoodleWithExtension(options: {
  readonly win?: Window;
  readonly timeoutMs?: number;
  readonly confirmConnected: () => Promise<boolean>;
}): Promise<MoodleExtensionResult> {
  const win = options.win ?? window;
  const timeoutMs = options.timeoutMs ?? CONNECT_TIMEOUT_MS;
  if (!isConnectorInstalled(win.document)) return Promise.resolve({ status: 'failed' });
  return new Promise((resolve) => {
    const finish = (status: MoodleExtensionStatus, name?: string): void => {
      win.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve({ status, ...(status === 'connected' && name !== undefined ? { moodleFullName: name } : {}) });
    };
    const onMessage = (event: MessageEvent): void => {
      const read = readStatus(event, win);
      if (read === undefined) return;
      if (read.status !== 'connected') {
        finish(read.status);
        return;
      }
      options.confirmConnected().then(
        (connected) => finish(connected ? 'connected' : 'failed', read.name),
        () => finish('failed'),
      );
    };
    const timer = setTimeout(() => finish('failed'), timeoutMs);
    win.addEventListener('message', onMessage);
    win.postMessage({ type: REQUEST_TYPE }, win.location.origin);
  });
}
